import React, { useMemo, useState } from 'react';
import { ChevronLeft, Search, SlidersHorizontal, X } from 'lucide-react';
import '../../pages/graph-section/GraphSection.css';
import { useIsPhone } from './PhoneNav';
import { fmt } from '../../nucleus/format';

// THE FIND BAR AND THE FILTER RAIL — above a collection, a search with a
// Filters button at its right; the button opens every other way to narrow the
// list (the rail at the left, or a sheet on a phone), each choice a row with
// its count as a bar. The search is never inside the rail: it is always there.
// Filters HIDE what does not match (a long list cannot be read by dimming).
// Within one facet choices add up (Paused or Lapsed); across facets they
// narrow (Paused and Paid). A count is what that row would show under the
// other filters. ⌥-press picks a row alone.
//
//   const f = useFacets(items, FACETS, { text: (it) => it.name });
//   <Faceted id="apps" rail={f.rail}> …render f.shown… </Faceted>
//
// A facet is { id, title, of(item) → key | [keys] | null, order?: [keys],
// label?(key) → word, always?: true }. A facet with fewer than two keys among
// the items is left out (nothing to choose between), unless it is `always`:
// then every key in its order stands, at 0 where nothing has it.

const SHOWN = 6;
const keysOf = (f, it) => { const k = f.of(it); return k == null ? [] : Array.isArray(k) ? k : [k]; };

export function useFacets(items, facets, { text, placeholder = 'Filter', initial } = {}) {
    const [search, setSearch] = useState('');
    const [picked, setPicked] = useState(() => initial || {}); // facet id → [keys]
    const q = search.trim().toLowerCase();
    const hitText = (it) => !q || String(text ? text(it) : '').toLowerCase().includes(q);
    const hitFacet = (f, it) => { const on = picked[f.id] || []; return !on.length || keysOf(f, it).some((k) => on.includes(k)); };

    const shown = useMemo(
        () => items.filter((it) => hitText(it) && facets.every((f) => hitFacet(f, it))),
        [items, facets, q, picked], // eslint-disable-line react-hooks/exhaustive-deps
    );

    // each facet's rows, counted under the search and every OTHER facet
    const rows = facets.map((f) => {
        const pool = items.filter((it) => hitText(it) && facets.every((g) => g === f || hitFacet(g, it)));
        const n = new Map();
        items.forEach((it) => keysOf(f, it).forEach((k) => { if (!n.has(k)) n.set(k, 0); }));
        if (f.always && items.length) (f.order || []).forEach((k) => { if (!n.has(k)) n.set(k, 0); });
        pool.forEach((it) => keysOf(f, it).forEach((k) => n.set(k, (n.get(k) || 0) + 1)));
        const order = f.order || [...n.keys()].sort((a, b) => n.get(b) - n.get(a) || String(a).localeCompare(String(b)));
        return { ...f, items: order.filter((k) => n.has(k)).map((k) => ({ key: k, label: f.label ? f.label(k) : String(k), count: n.get(k) })) };
    }).filter((f) => f.items.length > 1 || (f.always && f.items.length) || (picked[f.id] || []).length);

    const facetActive = Object.values(picked).filter((v) => v.length).length;
    const active = (q ? 1 : 0) + facetActive;
    const toggle = (id, k, only) => setPicked((p) => {
        const on = p[id] || [];
        const next = only ? (on.length === 1 && on[0] === k ? [] : [k]) : on.includes(k) ? on.filter((x) => x !== k) : [...on, k];
        return { ...p, [id]: next };
    });
    const clear = () => { setSearch(''); setPicked({}); };
    const clearFacets = () => setPicked({});
    // set one facet's picks outright (a figure above the list narrowing it)
    const pick = (next) => setPicked((p) => ({ ...p, ...next }));

    const rail = (
        <FacetRail search={search} onSearch={setSearch} placeholder={placeholder} facets={rows} picked={picked} onToggle={toggle}
            active={facetActive} onClear={clearFacets} />
    );
    return { shown, rail, active, clear, pick, picked, search, filtering: active > 0 };
}

/** The search above a collection, the Filters button at its left. `compact` is
 *  the same field inside a rail, a sheet or a panel: no bar around it. */
export function FindBar({ value, onChange, placeholder = 'Filter', label, filters, onFilters, active = 0, compact = false, autoFocus = false, inputRef, className = '' }) {
    return (
        <div className={`nx-findbar${compact ? ' is-compact' : ''}${className ? ` ${className}` : ''}`}>
            {onFilters && (
                <button type="button" className={`gx-rail__act nx-findbar__filters${filters ? ' is-on' : ''}`}
                    aria-expanded={!!filters} onClick={onFilters}>
                    <SlidersHorizontal size={14} aria-hidden="true" />Filters{active > 0 && <b>{active}</b>}
                </button>
            )}
            <label className="nx-findbar__field">
                <Search size={compact ? 13 : 14} strokeWidth={1.8} aria-hidden="true" />
                <input ref={inputRef} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={label || placeholder}
                    autoFocus={autoFocus} onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.preventDefault(); onChange(''); } }} />
            </label>
        </div>
    );
}

export function FacetRail({ facets, picked, onToggle, active, onClear, onCollapse, collapseWord = 'Collapse' }) {
    return (
        <section className="gx-rail nx-facets" aria-label="Filters">
            <RailHead active={active} onClear={onClear} onClose={onCollapse} closeWord={collapseWord} />
            {facets.map((f) => (
                <Facet key={f.id} title={f.title} items={f.items} selected={picked[f.id] || []} onToggle={(k, only) => onToggle(f.id, k, only)} />
            ))}
        </section>
    );
}

/** A rail's head: Clear (with how many are on) or "No filters", then the way
 *  to put the rail away: Collapse (beside the list), Close (over it) or Done (a sheet). */
export function RailHead({ active, onClear, onClose, closeWord = 'Collapse' }) {
    return (
        <div className="gx-rail__head">
            {active > 0
                ? <button type="button" className="gx-rail__act" onClick={onClear}>Clear<b>{active}</b></button>
                : <span className="gx-rail__none">No filters</span>}
            {onClose && (
                <button type="button" className="gx-rail__act" onClick={onClose} aria-label={closeWord === 'Done' ? 'Done filtering' : `${closeWord} the filters`}>
                    {closeWord === 'Done' ? null : closeWord === 'Close' ? <X size={13} aria-hidden="true" /> : <ChevronLeft size={13} aria-hidden="true" />}{closeWord}
                </button>
            )}
        </div>
    );
}

/** One part of a rail: its name in mono caps, anything beside it, then the part. */
export function RailSection({ title, aside, children }) {
    return (
        <section className="gx-sec">
            <div className="gx-sec__head"><span>{title}</span>{aside}</div>
            {children}
        </section>
    );
}

/** A histogram of one facet: each choice a row, its bar its count (a `tint`
 *  colours it). What was picked stays listed even when its count falls to
 *  nothing; a long facet, opened in full, can be searched. */
export function Facet({ title, items, selected, onToggle, loading = false, none }) {
    const [all, setAll] = useState(false);
    const [q, setQ] = useState('');
    const max = Math.max(1, ...items.map((i) => i.count));
    const known = new Set(items.map((i) => i.key));
    const rows = [...items, ...selected.filter((k) => !known.has(k)).map((k) => ({ key: k, label: String(k), count: 0 }))];
    const found = q ? rows.filter((r) => r.label.toLowerCase().includes(q.toLowerCase())) : rows;
    const shown = all || q ? found : found.slice(0, SHOWN).concat(found.slice(SHOWN).filter((r) => selected.includes(r.key)));
    return (
        <RailSection
            title={title}
            aside={selected.length > 0 && <button type="button" className="nx-link" onClick={() => selected.forEach((k) => onToggle(k))}>Clear</button>}
        >
            {loading && !rows.length && <div className="gx-facet__skel" aria-hidden="true"><i /><i /><i /></div>}
            {!loading && !rows.length && <p className="gx-quiet">{none || 'Nothing yet.'}</p>}
            {all && rows.length > 10 && (
                <FindBar compact autoFocus className="gx-facet__find" value={q} onChange={setQ} placeholder={`Find a ${title.toLowerCase()}`} label={`Find in ${title}`} />
            )}
            <div className="gx-facet" role="group" aria-label={title}>
                {shown.map((r) => {
                    const on = selected.includes(r.key);
                    return (
                        <button
                            key={r.key} type="button" role="checkbox" aria-checked={on}
                            className={`gx-bar${on ? ' is-on' : ''}${selected.length && !on ? ' is-off' : ''}`}
                            style={{ '--w': `${Math.max(r.count ? 3 : 0, (r.count / max) * 100)}%`, ...(r.tint ? { '--tint': r.tint } : null) }}
                            onClick={(e) => onToggle(r.key, e.altKey)}
                            title={`${r.label}: ${fmt(r.count)}${on ? '' : ' (⌥-press to pick only this)'}`}
                        >
                            <span className="gx-bar__box" aria-hidden="true" />
                            <span className="gx-bar__label">{r.label}</span>
                            <span className="gx-bar__n">{fmt(r.count)}</span>
                        </button>
                    );
                })}
            </div>
            {rows.length > SHOWN && (
                <button type="button" className="gx-more" onClick={() => { setAll((v) => !v); setQ(''); }}>
                    {all ? 'Fewer' : `All ${rows.length}`}
                </button>
            )}
        </RailSection>
    );
}

// THE PAGE WITH FILTERS: the find bar above the collection, always; its
// Filters button (at the bar's left) opens the rail at the left (remembered per
// page, closed at first) or, on a phone, a sheet over the page that Done closes.
// A page whose items give nothing to choose between shows the search alone.
// `notice` is what wants you on this page ("2 groups invited you"): it stands
// above the bar, the first thing read.
const filtersKey = (id) => `nucleus:filters:${id}`;
export function Faceted({ id, rail, notice = null, children }) {
    const { search, onSearch, placeholder, facets = [], active = 0 } = rail.props;
    const [open, setOpen] = useState(() => { try { return localStorage.getItem(filtersKey(id)) === 'open'; } catch { return false; } });
    const set = (v) => { setOpen(v); try { localStorage.setItem(filtersKey(id), v ? 'open' : 'closed'); } catch { /* private mode */ } };
    const phone = useIsPhone();
    const [sheet, setSheet] = useState(false);
    const any = facets.length > 0;
    const shown = any && (phone ? sheet : open);
    const options = React.cloneElement(rail, phone
        ? { onCollapse: () => setSheet(false), collapseWord: 'Done' }
        : { onCollapse: () => set(false), collapseWord: 'Collapse' });
    return (
        <div className={`nx-faceted${!phone && shown ? ' has-rail' : ''}${phone ? ' is-phone' : ''}`}>
            {!phone && shown && <div className="nx-faceted__rail">{options}</div>}
            {phone && shown && <div className="nx-faceted__sheet" role="dialog" aria-modal="true" aria-label="Filters">{options}</div>}
            <div className="nx-faceted__main">
                {notice}
                <FindBar value={search} onChange={onSearch} placeholder={placeholder} active={active} filters={shown}
                    onFilters={any ? () => (phone ? setSheet(true) : set(!open)) : undefined} />
                {children}
            </div>
        </div>
    );
}
